import * as React from 'react'

import { sectionSchemaRegistry } from '@/lib/section-schema-registry'
import { cn } from '@/lib/utils'
import useLocale from '@/hooks/use-locale'

import { instructors as defaultInstructorsVi } from '@/data/instructors'
import { instructorsEng as defaultInstructorsEn } from '@/data/instructors-en'

import InstructorCard from '@/components/cards/instructor-card'
import SectionHeader from '@/components/common/section-header'

import type { SectionElementSchema } from '@/types/element-schema.type'

interface InstructorItem {
  id?: string | number
  username: string
  position: string
  description: string
  avatar: string
  transform?: string
  linkedin: string
  course: string
  module?: string
  status?: string
}

interface LecturersSectionV2Props {
  className?: string
  /** Section heading */
  heading?: string
  /** Section subheading */
  subheading?: string
  /** Heading text color */
  headingColor?: string
  /** Subheading text color */
  subheadingColor?: string
  /** Data source: 'default' uses built-in data, 'custom' uses instructors array */
  dataSource?: 'default' | 'custom'
  /** Custom instructors (when dataSource='custom') */
  instructors?: InstructorItem[]
  /** Grid columns for layout */
  columns?: 2 | 3 | 4
  /** Background color */
  bgColor?: string
  /** Show active only or all instructors (default mode) */
  showStatus?: 'active' | 'all'
  /** Children content (instructor cards) — overrides internal rendering */
  children?: React.ReactNode
}

const defaultInstructorItems: InstructorItem[] = [
  {
    id: 1,
    username: 'Giảng viên mẫu',
    position: 'Senior Engineer',
    description: 'Mô tả ngắn về giảng viên.',
    avatar: '/images/founder.webp',
    transform: '',
    linkedin: '#',
    course: 'Module',
  },
]

// Element schema for Puck editor
export const lecturersSectionV2Schema: SectionElementSchema = {
  componentType: 'LecturersSectionV2',
  label: 'Lecturers Section',
  category: 'content',
  description: 'Instructors/lecturers section with header and customizable layout',
  elements: [
    {
      key: 'heading',
      type: 'textarea',
      label: 'Section Heading',
      helpText: 'Main title of the section',
    },
    {
      key: 'subheading',
      type: 'textarea',
      label: 'Section Subheading',
      helpText: 'Optional subtitle below the heading',
    },
    {
      key: 'headingColor',
      type: 'color',
      label: 'Heading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'subheadingColor',
      type: 'color',
      label: 'Subheading Color',
      helpText: 'Leave empty for default theme color',
      default: '',
    },
    {
      key: 'bgColor',
      type: 'color',
      label: 'Background Color',
      default: 'transparent',
    },
    {
      key: 'dataSource',
      type: 'select',
      label: 'Data Source',
      helpText: 'Use built-in default instructors or provide custom ones',
      options: [
        { label: 'Default (Built-in)', value: 'default' },
        { label: 'Custom', value: 'custom' },
      ],
      default: 'default',
    },
    {
      key: 'showStatus',
      type: 'select',
      label: 'Filter by Status',
      helpText: 'Show only active instructors or all (default mode only)',
      options: [
        { label: 'Active Only', value: 'active' },
        { label: 'All', value: 'all' },
      ],
      default: 'active',
    },
    {
      key: 'columns',
      type: 'select',
      label: 'Grid Columns',
      helpText: 'Number of columns in the instructor grid',
      options: [
        { label: '2 Columns', value: 2 },
        { label: '3 Columns', value: 3 },
        { label: '4 Columns', value: 4 },
      ],
      default: 3,
    },
    {
      key: 'instructors',
      type: 'array',
      label: 'Custom Instructors',
      helpText: 'Add, edit, or remove instructor cards (only when Data Source is "Custom")',
      default: defaultInstructorItems,
      children: [
        { key: 'username', type: 'text', label: 'Name', required: true },
        { key: 'position', type: 'text', label: 'Position', required: true },
        { key: 'description', type: 'textarea', label: 'Description', required: true },
        { key: 'avatar', type: 'image', label: 'Avatar URL', required: true },
        { key: 'linkedin', type: 'text', label: 'LinkedIn URL' },
        { key: 'course', type: 'text', label: 'Course / Module' },
      ],
    },
    {
      key: 'className',
      type: 'text',
      label: 'CSS Class',
      helpText: 'Additional CSS classes',
    },
  ],
}

// Register schema
sectionSchemaRegistry.register(lecturersSectionV2Schema)

const columnClassMap: Record<number, string> = {
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
}

function LecturersSectionV2({
  className,
  heading,
  subheading,
  headingColor,
  subheadingColor,
  dataSource = 'default',
  instructors: customInstructors,
  columns = 3,
  bgColor = 'transparent',
  showStatus = 'active',
  children,
}: React.PropsWithChildren<LecturersSectionV2Props>): JSX.Element {
  const { text, lang } = useLocale()

  const displayHeading = heading || text.MAIN.BODY.HEADING_10

  // Determine which instructors to display
  const builtInInstructors = lang === 'vi' ? defaultInstructorsVi : defaultInstructorsEn
  const useCustom = dataSource === 'custom' && customInstructors?.length
  const filteredBuiltIn =
    showStatus === 'active' ? builtInInstructors.filter(i => i?.status === 'active') : builtInInstructors

  const displayInstructors: InstructorItem[] = useCustom
    ? customInstructors.map((item, index) => ({ ...item, id: item.id ?? index + 1, transform: item.transform ?? '' }))
    : filteredBuiltIn

  const gridColsClass = columnClassMap[columns] || 'lg:grid-cols-3'

  const defaultContent = (
    <div
      className={cn('grid grid-cols-1 gap-4 md:grid-cols-2 sm:gap-6 md:gap-8 xl:gap-10 lg:mt-16 mt-8', gridColsClass)}
    >
      {displayInstructors.map((instructor, index) => (
        <div
          key={instructor.id ?? index}
          className={`flex justify-center ${
            displayInstructors.length === 1 ||
            (displayInstructors.length % columns === 1 && index === displayInstructors.length - 1)
              ? columns === 3
                ? 'lg:col-start-2'
                : ''
              : ''
          }`}
        >
          <InstructorCard
            username={instructor.username}
            position={instructor.position}
            description={instructor.description}
            avatar={instructor.avatar}
            transform={instructor.transform || ''}
            linkedin={instructor.linkedin || '#'}
            course={useCustom ? instructor.course || '' : instructor.module || instructor.course || ''}
          />
        </div>
      ))}
    </div>
  )

  const hasCustomColors = headingColor || subheadingColor

  return (
    <div
      className={cn('container', className)}
      id="instructors"
      style={{ backgroundColor: bgColor !== 'transparent' ? bgColor : undefined }}
    >
      <section className="mt-16 sm:mt-8 sm:pt-20 pt-8" id="teachers">
        {hasCustomColors ? (
          <div className="flex flex-col items-center text-center">
            <h2
              className="text-3xl font-semibold md:text-4xl lg:text-5xl"
              style={headingColor ? { color: headingColor } : undefined}
            >
              {displayHeading}
            </h2>
            {subheading && (
              <p
                className="mt-6 text-balance text-lg md:text-xl text-muted-foreground max-w-[46.875rem]"
                style={subheadingColor ? { color: subheadingColor } : undefined}
              >
                {subheading}
              </p>
            )}
          </div>
        ) : (
          <SectionHeader title={displayHeading} subtitle={subheading} />
        )}
      </section>
      {children || defaultContent}
    </div>
  )
}

export default LecturersSectionV2
